import * as R from './routes';
import {
  getNextDRScreen,
  getRepeatedLoanDefaultScreen,
} from 'utils/drRedirection';
import { isNotProduction } from 'utils/isNotProduction';
import { isProductKind } from 'utils/getProductNumber';
import { isRepaymentRouteDisabled } from 'hooks/useMenuItems';
import { isUserAffectedByDana } from 'pages/Dana/Dana';
import { isVoluntaryBannerAvailable } from 'utils/voluntaryBanner';
import { Navigate } from 'react-router-dom';
import { PENDING_BUNDLE_FEE, PRODUCT_NUMBER_IL } from 'utils/constants';
import { ReactNode, useEffect, useState } from 'react';
import {
  useActiveCard,
  useApplication,
  useClient,
  useClientBlockedStatus,
  useClientSettings,
  useLatestLoan,
} from 'hooks/webapi';
import { useActiveLoan } from 'pages/ActiveLoan/useActiveLoan';
import { userIsNotIdentified } from 'utils/userIsNotIdentified';
import { useTranslation } from 'react-i18next';
import isLogged from 'utils/isLogged';
import styles from './GuardRoute.module.scss';
import useApplicationProposals from 'hooks/webapi/useApplicationProposals';
import useBundle from 'hooks/useBundle';
import useClientDebtRestructuring from 'hooks/webapi/useClientDebtRestructuring';

type Props = {
  children: ReactNode;
  path: string;
};

export default function GuardRoute({ children, path }: Props) {
  const { t } = useTranslation('error');
  const [isRouteAvailableFromBE, setIsRouteAvailableFromBE] = useState<
    boolean | undefined
  >(true);
  const [initialRouteChecked, setInitialRouteChecked] = useState<
    string | undefined
  >();

  const { activeCard } = useActiveCard();
  const { isOverdue, isBrokenAgreement } = useActiveLoan();
  const { debtRestructuringData, isDebtRestructuring, isPaymentReceived } =
    useClientDebtRestructuring();
  const { client } = useClient();
  const { clientSettings } = useClientSettings();
  const { application } = useApplication();
  const { applicationProposals } = useApplicationProposals();
  const { latestLoan } = useLatestLoan();
  const { clientBlockedStatus } = useClientBlockedStatus();
  const { bundleFee, isBundleAvailable, isBundleAccepted } = useBundle();
  const bundleFeeAvailable = bundleFee?.bundleFee !== undefined;

  const isDataMissed = !client || !application || !clientBlockedStatus;

  const isClientLogged = isLogged();

  const checkBEConditions = async () => {
    if (!isClientLogged || isDataMissed) return true;
    const isExtension =
      latestLoan &&
      latestLoan.loanExtensionFees &&
      latestLoan.loanExtensionFees.length > 1;

    const userNotIdentified = userIsNotIdentified(client);

    const voluntaryBannerAvailable = isVoluntaryBannerAvailable(client);
    const pendingDocuments = userNotIdentified || voluntaryBannerAvailable;

    switch (path) {
      case R.ACTIVE_LOAN:
        return Boolean(latestLoan);
      case R.REPEATED_LOAN:
      case R.LOAN_CONFIRM:
        return !latestLoan && !clientBlockedStatus?.blocked;
      case R.LOAN_CHECK_DETAILS:
        return !latestLoan;
      case R.LOAN_APPROVED:
        return Boolean(
          application?.confirmed && application?.resolution === 'APPROVED',
        );
      case R.LOAN_FAILED:
      case R.LOAN_REJECTED:
        return Boolean(
          application?.confirmed && application?.resolution !== 'APPROVED',
        );
      case R.ACCOUNT_BEST_OFFER:
      case R.BEST_OFFER:
        const isBestOfferExpired = !applicationProposals?.bestOfferProposal;
        return Boolean(
          !isBestOfferExpired && application?.resolutionDetail === 'NEW_OFFER',
        );
      case R.ADDITIONAL_AMOUNT:
      case R.ADDITIONAL_AMOUNT_CONFIRM:
      case R.ADDITIONAL_AMOUNT_CHECK_DETAILS:
        return Boolean(
          client?.additionalAmount && !latestLoan?.debtCollectLoanAgreement,
        );
      case R.ADDITIONAL_AMOUNT_APPROVED:
        return Boolean(application?.resolution === 'APPROVED');
      case R.ADDITIONAL_AMOUNT_FAILED:
      case R.ADDITIONAL_AMOUNT_REJECTED:
        return Boolean(application?.resolution !== 'APPROVED');
      case R.NEWS_CONSENT:
        return !clientSettings?.acceptNews;
      case R.REPAYMENT:
        return !isRepaymentRouteDisabled(latestLoan, debtRestructuringData);
      case R.FULL_REPAYMENT:
        return (
          !isBrokenAgreement &&
          !isRepaymentRouteDisabled(latestLoan, debtRestructuringData)
        );
      case R.REPAYMENT_WIRE_TRANSFER:
        return Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement);
      case R.EXTENSION_WIRE_TRANSFER:
        return Boolean(
          latestLoan &&
            !latestLoan.debtCollectLoanAgreement &&
            latestLoan.loanExtensionFees &&
            latestLoan.loanExtensionFees.length,
        );
      case R.EXTENSIONS:
      case R.EXTENSION_PAYMENT:
      case R.EXTENSION_WITH_CARD:
      case R.EXTENSION_LIMITS_REJECTED:
      case R.EXTENSION_REJECTED:
      case R.EXTENSION_SUCCESS:
        return Boolean(isExtension);
      case R.INSTALLMENT_PAYMENT:
        return (
          !latestLoan?.debtCollectLoanAgreement &&
          (isProductKind(latestLoan, PRODUCT_NUMBER_IL) || isDebtRestructuring)
        );
      case R.CARD_PAYMENT_CONS_SUCCESS:
      case R.CARD_PAYMENT_CONS_FAILURE:
        return (
          isProductKind(latestLoan, PRODUCT_NUMBER_IL) || isDebtRestructuring
        );
      case R.CARD_PAYMENT_MINI_SUCCESS:
        return latestLoan?.productCode === 'MINI';
      case R.DR_PAYMENT_SCHEDULE:
      case R.DR_OFFER_ACCEPTED:
        return (
          isDebtRestructuring &&
          Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement)
        );
      case R.DR_OFFER:
        return (
          isDebtRestructuring &&
          debtRestructuringData?.status === 'ELIGIBLE' &&
          Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement)
        );
      case R.DR_OFFER_CONS:
        return (
          isDebtRestructuring &&
          isProductKind(latestLoan, PRODUCT_NUMBER_IL) &&
          Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement)
        );
      case R.DR_OVERDUE:
        return (
          isDebtRestructuring &&
          Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement)
        );
      case R.DR_PAYMENT:
        return isDebtRestructuring && !isPaymentReceived;
      case R.DOCUMENTS_PENDING:
      case R.DOCUMENTS_APPROVED:
        return pendingDocuments || client?.hasAttachments;
      case R.REPAYMENT_BROKEN_LOAN:
        return Boolean(latestLoan) && isBrokenAgreement;
      case R.SIGN_DR_AGREEMENT:
        return Boolean(
          debtRestructuringData && !debtRestructuringData?.isSigned,
        );
      case R.SIGN_DR_AGREEMENT_SUCCESS:
        return Boolean(
          debtRestructuringData && debtRestructuringData?.isSigned,
        );
      case R.SAVE_CARD_MAIN:
      case R.SAVE_CARD_ADDITIONAL:
        return Boolean(!activeCard);
      case R.AUTOREPAY_MAIN:
      case R.AUTOREPAY_ADDITIONAL:
        return Boolean(activeCard);
      case R.BREAKDOWN_DEBT:
        return (
          isProductKind(latestLoan, PRODUCT_NUMBER_IL) &&
          !isOverdue &&
          Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement)
        );
      case R.DANA:
        const isDanaUser = isUserAffectedByDana();
        return isDanaUser;
      case R.BUNDLE_VIVUSPLUS_OFFER:
      case R.BUNDLE_VIVUSPLUS_PENDING:
        return (
          isBundleAvailable &&
          application?.resolutionDetail === PENDING_BUNDLE_FEE
        );
      case R.BUNDLE_VIVUSPLUS_CONFIRMED:
      case R.BUNDLE_VIVUSPLUS_CONFIRMED_AA:
      case R.BUNDLE_VIVUSPLUS_NO_CONFIRMED:
      case R.BUNDLE_VIVUSPLUS_NO_CONFIRMED_AA:
        return isBundleAvailable;
      case R.BUNDLE_VIVUSPLUS_BENEFITS:
      case R.BUNDLE_VIVUSPLUS_FAQ:
        return isBundleAccepted && !isBrokenAgreement;
      case R.CARD_PAYMENT:
        return Boolean(latestLoan && !latestLoan.debtCollectLoanAgreement);
      case R.REJECTED_TIMER:
        return clientBlockedStatus?.blocked;
      case R.INSURANCE_POLICY:
        return Boolean(application?.insurancePrice) && !application?.confirmed;
      case R.ONLINE_LOGIN:
      case R.LOGIN:
      case R.TWO_FACTOR_AUTH:
      case R.TWO_2FA_PHONE_MODIFIED:
      case R.CHANGE_PHONE_TWO_FACTOR_AUTH:
      case R.TWO_2FA_PHONE_ERROR:
      case R.ONE_TIME_TOKEN_LOGIN:
      case R.TWO_2FA_PHONE:
      case R.CARD_PAYMENT_REJECTED_DUE_DUPLICATE:
      case R.CARD_PAYMENT_FAILURE:
      case R.PROFILE:
      case R.PASSWORD_CHANGE:
      case R.PASSWORD_CHANGE_SUCCESS:
      case R.CHANGE_TEMPORARY_PASSWORD:
      case R.PASSWORD_RECOVERY:
      case R.PASSWORD_RECOVERY_SUCCESS:
      case R.FAQ:
      case R.EXTENSION_CARD_PAYMENT_REJECTED_DUE_DUPLICATE:
      case R.CARD_PAYMENT_FAILURE_BY_CARD:
      case R.UNSUBSCRIPTION_PAGE:
      case R.LOAN_CHECK_DETAILS_INSURANCE:
      case R.CHECK_DOCUMENTS:
      case R.SELEKTRA_ROUTE:
      case R.DOCUMENTS_PENDING_UPLOAD:
        return true;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    const fetchConditions = async () => {
      if (isDataMissed) {
        if (!isRouteAvailableFromBE) setIsRouteAvailableFromBE(true);
        return;
      }

      let nextIsRouteAvailableFromBE: boolean | undefined = true;
      if (initialRouteChecked !== path) {
        nextIsRouteAvailableFromBE = await checkBEConditions();
        if (nextIsRouteAvailableFromBE) setInitialRouteChecked(path);
      }

      if (isRouteAvailableFromBE !== nextIsRouteAvailableFromBE)
        setIsRouteAvailableFromBE(nextIsRouteAvailableFromBE);
    };
    fetchConditions();
  }, [
    application,
    applicationProposals,
    bundleFeeAvailable,
    client,
    clientSettings,
    isBrokenAgreement,
    isBundleAvailable,
    isBundleAccepted,
    latestLoan,
    path,
  ]);

  const dashboardRedirectionRoute = Boolean(latestLoan)
    ? getNextDRScreen(debtRestructuringData, latestLoan)
    : getRepeatedLoanDefaultScreen(clientBlockedStatus, latestLoan);

  if (isRouteAvailableFromBE === false) {
    return <Navigate to={dashboardRedirectionRoute} replace />;
  }

  return (
    <div className={styles['wrapper-guardRoute']} key={path}>
      {isNotProduction() && isRouteAvailableFromBE === undefined && (
        <div className={styles.banner}>{t('routerBanner')}</div>
      )}
      {children}
    </div>
  );
}
